import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { Component, ChangeDetectorRef, OnDestroy, Optional } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';

import { SnPasswordAPI } from '@lib/api/services';
import { snFadeInDown } from '@lib/common/animations';
import { t } from '@lib/translation/helpers';
import { TranslationService } from '@lib/translation/services';

import { IResetPasswordFormModel } from '../../components/reset-password-form';

@Component({
  selector: 'sn-reset-password',
  templateUrl: './reset-password.view.html',
  styleUrls: ['./reset-password.view.scss'],
  animations: [snFadeInDown],
})
export class ResetPasswordView implements OnDestroy {
  public readonly form: UntypedFormGroup;
  public readonly formModel: IResetPasswordFormModel;

  public isLoading = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    activatedRoute: ActivatedRoute,
    private readonly _router: Router,
    private readonly _passwordAPI: SnPasswordAPI,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _snackbar: MatSnackBar,
    private readonly _formBuilder: UntypedFormBuilder,
    @Optional() private readonly _translationService: TranslationService,
  ) {
    const emailFromParams = activatedRoute.snapshot.queryParams.email || '';
    
    this.form = this._formBuilder.group({
      email: [emailFromParams, [Validators.required]]
    });
    
    this.formModel = {
      email: emailFromParams,
    };
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onFormSubmit(): void {
    this.isLoading = true;
    this._cdr.markForCheck();

    this._passwordAPI
      .resetPasswordLink(this.form.value.email)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          this.isLoading = false;
          this._cdr.markForCheck();
        }),
      )
      .subscribe({
        next: this._onSuccess.bind(this),
        error: this._onError.bind(this),
      });
  }

  private _onSuccess(): void {
    const successMessage = t('auth.resetPassword.successMessage');
    this._snackbar.open(successMessage);
    this._router.navigate(['/auth', 'sign-in'], {
      queryParams: {
        email: this.form.value.email,
      },
    });
  }

  private _onError(errorResponse: HttpErrorResponse): void {
    if (errorResponse.status === 400) {
      this.form.setErrors(errorResponse.error);
    }
  }
}
